/**
 * Auth data layer — org memberships for the in-app switcher.
 * Org-admin roster/invite live in OrganizationAdminSection.
 */

import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { APIError } from '@localagent/sdk';
import type { OrgMembership, ListMembersResponse } from '@localagent/sdk';
import { getSDK } from '../services/sdk-instance';
import { holdsIdentityAtom } from '../stores/auth/atoms';
import { sdkConfiguredAtom } from '../stores/sdk/atoms';

const ORGANIZATIONS_KEY = ['auth-organizations'] as const;

export function useOrganizations() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  return useQuery<OrgMembership[], Error>({
    queryKey: ORGANIZATIONS_KEY,
    queryFn: () => getSDK().auth.listOrganizations(),
    enabled: Boolean(isAuthenticated && sdkConfigured),
  });
}

/**
 * Is the caller an org admin? The session carries no roles (account
 * ADR-020 / authz ADR-007) — the only source of truth is the server, which
 * `authz.Authorize(..., ActionOrgAdmin, ...)` already enforces on every
 * org-admin-gated write. This probes the cheapest such read
 * (`GET /v1/auth/organizations/{id}/members`, limit 1) and treats a 403 as
 * "not an org admin" rather than an error — the same probe-and-403 idiom
 * `RoleManagementSection` already uses for the account tier. A network/5xx
 * failure also renders as "not admin" (fail-closed): the UI section this
 * gates has no independent authority — the real gate is server-side on the
 * BYOK routes themselves, so hiding it on an ambiguous read is the safe
 * default, never an unauthorized action.
 */
export function useIsOrgAdmin(organizationId: string | null | undefined) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  const query = useQuery<ListMembersResponse, Error>({
    queryKey: ['auth-org-admin-probe', organizationId],
    queryFn: () => getSDK().auth.listMembers(organizationId!, { limit: 1 }),
    enabled: Boolean(organizationId && isAuthenticated && sdkConfigured),
    retry: (failureCount, err) =>
      !(err instanceof APIError && err.status === 403) && failureCount < 2,
  });

  return { isOrgAdmin: query.isSuccess, isLoading: query.isLoading };
}
